import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Bar, Line } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import API from "../../common/API"

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
)

interface SummaryData {
  organizations: number
  users: number
  denuncias: number
  fichados: number
  paDep: number
  deportados: number
  extraditados: number
  desaparecidos: number
  extranjeros: number
  litigios: number
  estructuras: number
  audiencias: number
  partes: number
  noticias: number
  activityByDay: { date: string; count: number }[]
  recentActivity: {
    userName: string
    section: string
    action: string
    date: string
    ipAddress: string
  }[]
}

const KPICard = ({
  title,
  value,
  icon,
  color,
}: {
  title: string
  value: number
  icon: string
  color: string
}) => (
  <div className="col-6 col-md-4 col-lg-3 mb-3">
    <div className={`card border-0 shadow-sm h-100`}>
      <div className="card-body d-flex align-items-center gap-3 p-3">
        <div
          className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0`}
          style={{ width: 48, height: 48, background: color + "22" }}
        >
          <i className={`${icon} fa-lg`} style={{ color }} />
        </div>
        <div className="overflow-hidden">
          <div
            className="fw-bold text-truncate"
            style={{ fontSize: "1.4rem", lineHeight: 1.1 }}
          >
            {value.toLocaleString()}
          </div>
          <div className="text-muted small text-truncate">{title}</div>
        </div>
      </div>
    </div>
  </div>
)

const AdminDashboardComponent = () => {
  const { t } = useTranslation("global")
  const [data, setData] = useState<SummaryData | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const bindData = async () => {
    try {
      setIsLoading(true)
      const res = await API.getAction("dashboard/AdminSummary")
      if (res.data.status === "ok") {
        setData(res.data.response)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    bindData()
  }, [])

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    )
  }

  if (!data) return null

  const barChartData = {
    labels: [
      t("Fichados"),
      "PADEP",
      t("Deportados"),
      t("Extraditados"),
      t("Desaparecidos"),
      t("Extranjeros"),
      t("Litigios"),
      t("Estructuras"),
    ],
    datasets: [
      {
        label: t("Registros"),
        data: [
          data.fichados,
          data.paDep,
          data.deportados,
          data.extraditados,
          data.desaparecidos,
          data.extranjeros,
          data.litigios,
          data.estructuras,
        ],
        backgroundColor: [
          "#ef444488",
          "#f9731688",
          "#eab30888",
          "#22c55e88",
          "#3b82f688",
          "#8b5cf688",
          "#ec489988",
          "#14b8a688",
        ],
        borderColor: [
          "#ef4444",
          "#f97316",
          "#eab308",
          "#22c55e",
          "#3b82f6",
          "#8b5cf6",
          "#ec4899",
          "#14b8a6",
        ],
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  }

  const lineChartData = {
    labels: data.activityByDay.map(x => x.date),
    datasets: [
      {
        label: t("Actividad"),
        data: data.activityByDay.map(x => x.count),
        borderColor: "#3b82f6",
        backgroundColor: "#3b82f620",
        fill: true,
        tension: 0.4,
        pointRadius: 4,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  }

  return (
    <div>
      <h5 className="mb-3 fw-bold">Dashboard</h5>

      {/* Platform KPIs */}
      <p className="text-muted small mb-2 text-uppercase fw-semibold">
        {t("Plataforma")}
      </p>
      <div className="row mb-2">
        <KPICard
          title={t("Organizaciones")}
          value={data.organizations}
          icon="fas fa-building"
          color="#3b82f6"
        />
        <KPICard
          title={t("Usuarios")}
          value={data.users}
          icon="fas fa-users"
          color="#8b5cf6"
        />
        <KPICard
          title={t("Denuncias")}
          value={data.denuncias}
          icon="fas fa-flag"
          color="#f97316"
        />
      </div>

      {/* Criminal data KPIs */}
      <p className="text-muted small mb-2 text-uppercase fw-semibold">
        {t("Datos Criminales")}
      </p>
      <div className="row mb-4">
        <KPICard
          title={t("Fichados")}
          value={data.fichados}
          icon="fas fa-fingerprint"
          color="#ef4444"
        />
        <KPICard
          title="PADEP"
          value={data.paDep}
          icon="fas fa-user-secret"
          color="#dc2626"
        />
        <KPICard
          title={t("Deportados")}
          value={data.deportados}
          icon="fas fa-plane-departure"
          color="#eab308"
        />
        <KPICard
          title={t("Extraditados")}
          value={data.extraditados}
          icon="fas fa-gavel"
          color="#f97316"
        />
        <KPICard
          title={t("Desaparecidos")}
          value={data.desaparecidos}
          icon="fas fa-question-circle"
          color="#6366f1"
        />
        <KPICard
          title={t("Extranjeros")}
          value={data.extranjeros}
          icon="fas fa-passport"
          color="#14b8a6"
        />
        <KPICard
          title={t("Litigios")}
          value={data.litigios}
          icon="fas fa-balance-scale"
          color="#ec4899"
        />
        <KPICard
          title={t("Estructuras Delictivas")}
          value={data.estructuras}
          icon="fas fa-network-wired"
          color="#22c55e"
        />
        <KPICard
          title={t("Audiencias")}
          value={data.audiencias}
          icon="fas fa-landmark"
          color="#0ea5e9"
        />
        <KPICard
          title={t("Partes")}
          value={data.partes}
          icon="fas fa-user-friends"
          color="#7c3aed"
        />
        <KPICard
          title={t("Noticias")}
          value={data.noticias}
          icon="fas fa-newspaper"
          color="#64748b"
        />
      </div>

      {/* Charts */}
      <div className="row mb-4">
        <div className="col-12 col-lg-7 mb-3 mb-lg-0 d-none">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">{t("Cobertura por Módulo")}</h6>
              <Bar data={barChartData} options={chartOptions} />
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-5">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">
                {t("Actividad — Últimos 7 días")}
              </h6>
              {data.activityByDay.length > 0 ? (
                <Line data={lineChartData} options={chartOptions} />
              ) : (
                <p className="text-muted small">{t("Sin actividad reciente")}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <h6 className="fw-semibold mb-3">{t("Actividad Reciente")}</h6>
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t("Usuario")}</th>
                  <th>{t("Sección")}</th>
                  <th>{t("Acción")}</th>
                  <th>{t("Fecha")}</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {data.recentActivity.map((item, i) => (
                  <tr key={i}>
                    <td>{item.userName ?? "—"}</td>
                    <td>
                      <span className="badge bg-light text-dark">
                        {item.section}
                      </span>
                    </td>
                    <td>{item.action}</td>
                    <td className="text-muted small">
                      {new Date(item.date).toLocaleString()}
                    </td>
                    <td className="text-muted small">{item.ipAddress}</td>
                  </tr>
                ))}
                {data.recentActivity.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-3">
                      {t("Sin actividad reciente")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboardComponent
